import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import * as Styles from "../../styles.ts";
import { groupBy } from "../../../util/group-by.ts";
import { reduce } from "@gordonb/generator";
import { repeat } from "lit/directives/repeat.js";

export type Segment = {
  id: string;
  label: string;
  value: number;
  color: string;
};

export type Datapoint = {
  id: string;
  label: string;
  color: string;
  value: number;
};

/** Convert discrete datapoints into segments for the distbar */
export const calcSegments = (datapoints: Iterable<Datapoint>): Segment[] => {
  const groups = groupBy(datapoints, (dp) => dp.id);
  const entries = Object.entries(groups);
  const segments: Segment[] = [];

  const total = reduce(datapoints, (acc, dp) => acc + dp.value, 0);

  for (const [id, group] of entries) {
    const groupTotal = reduce(group, (acc, dp) => acc + dp.value, 0);
    const segment = {
      id,
      label: group[0].label,
      value: groupTotal / total,
      color: group[0].color,
    };
    segments.push(segment);
  }

  return segments;
};

const SegmentView = (segment: Segment) => {
  const styles = styleMap({
    width: `${segment.value * 100}%`,
    backgroundColor: segment.color,
    boxShadow: `0 0 12px ${segment.color ?? "#fff"}`,
  });

  return html`
    <div
      class="distbar-segment"
      style="${styles}"
      title="${segment.label}"
    ></div>
  `;
};

const styles = css`
  :host {
    --distbar-height: var(--space-2);
    display: block;
    height: var(--distbar-height);
  }

  .distbar {
    background: var(--background);
    height: var(--distbar-height);
    border-radius: calc(var(--distbar-height) / 2);
    display: flex;
    flex-direction: row;
  }

  .distbar-segment {
    transition: width 1s ease-in-out;
  }

  .distbar-segment:first-child {
    border-top-left-radius: calc(var(--distbar-height) / 2);
    border-bottom-left-radius: calc(var(--distbar-height) / 2);
  }

  .distbar-segment:last-child {
    border-top-right-radius: calc(var(--distbar-height) / 2);
    border-bottom-right-radius: calc(var(--distbar-height) / 2);
  }
`;

@customElement("dist-bar")
export class DistabarElement extends LitElement {
  static override styles = [Styles.base, styles];

  @property({ type: Object })
  accessor datapoints: Datapoint[] = [];

  override render() {
    const segments = calcSegments(this.datapoints);

    return html`
      <div class="distbar">
        ${repeat(segments, (segment) => segment.id, SegmentView)}
      </div>
    `;
  }
}
